/* eslint-disable no-param-reassign */
// The `.current` writes below are on React refs passed in via ctx
// (sessionIDRef/actionIDRef/imageMetaRef). Mutating a ref's `.current` is its
// intended API — these refs bridge this call to the botLoading response handler
// and the image-upload analytics — so no-param-reassign is disabled for the file.
import {
  MESSAGE_ORIGIN_UNSPECIFIED,
  RETRY_MESSAGE,
  LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE,
  LIVE_AGENT_ERROR_MESSAGE,
  UPLOAD_ERROR_MESSAGES,
} from '../../shared/constants';
import { createActionId } from '../../platform/session/session';
import { getMcvisID, getSvocId } from '../../shared/utils/window-utils';
import { getContentModeration, getImageDimensions } from '../../shared/utils/common';
import { redactSensitiveContent } from '../../shared/utils/redaction';
import { isImageUrlValid } from '../../shared/utils/file-upload-utils';
import { emit } from '../../platform/events/lifecycle';
import { MessageType, createMessage } from '../../shared/message';
import { logger } from '../../shared/utils/logger';

/**
 * Orchestrates a user question: redaction gate → image moderation → routes to the
 * live agent (image / resume / active) or the Planner bot, saving messages and
 * firing analytics along the way. Extracted verbatim from AssistantOrchestrator; the
 * provider builds `ctx` from its hooks/refs/store actions and calls this.
 *
 * @param {object} input  { userQuestion, messageOrigin?, entryLocationForAnalytics?,
 *                          pageType?, retryStorageLink?, retryImageId? }
 * @param {object} ctx    provider-supplied capabilities (session, hooks, refs, store actions)
 * @returns {Promise<any>|{blocked:true}|false}
 */
export const runAskUserQuestion = async (input, ctx) => {
  const {
    userQuestion,
    messageOrigin = MESSAGE_ORIGIN_UNSPECIFIED,
    entryLocationForAnalytics,
    pageType,
    retryStorageLink,
    retryImageId,
  } = input;
  const {
    session,
    hosts,
    imageData,
    getIsEscalated,
    sessionIDRef,
    actionIDRef,
    imageMetaRef,
    messageHistoryRef,
    openDrawer,
    messages,
    setIsSubmitting,
    setUploadError,
    setHistoryCleared,
    saveMessage,
    submitUserQuestionToApi,
    sendAgentMessage,
    sendAgentImage,
    updateLastActivity,
    handleIdleDisconnect,
    checkIdleExpired,
    clearImage,
    broadCastMessageUpdate,
  } = ctx;

  const saveRetryMessage = async (skip) => {
    if (!skip && messages.at(-1)?.type === 'user') {
      return saveMessage(RETRY_MESSAGE);
    }
    return Promise.resolve();
  };

  // Centralized sensitive-data gate — blocks card/SSN regardless of caller.
  const { redacted, hasSensitive, error: redactError } = redactSensitiveContent(userQuestion);
  if (hasSensitive) {
    setIsSubmitting(false);
    return { blocked: true, redacted, error: redactError };
  }

  const sessionID = session.sessionId;
  // NOTE: This value for actionID is intended to be used as the single-source-of-truth for the
  // rest of askUserQuestion
  const actionID = createActionId(session.sessionId);
  sessionIDRef.current = sessionID;
  actionIDRef.current = actionID;
  let imageId = retryImageId || null;
  let storageLink = retryStorageLink || null;
  imageMetaRef.current = null;

  if (imageData && !getIsEscalated()) {
    imageMetaRef.current = {
      imageOrigin: imageData.imageOrigin,
      imageFileType: imageData.imageFileType,
      fileSize: imageData.fileSize
    };
    const uploadContext = {
      userContext: {
        userIdentities: {
          mcvisId: getMcvisID(),
          svocId: getSvocId()
        },
        inReplyToChatMessageId: '',
        caseId: '',
        zipCode: '',
        deviceType: '',
        actionId: actionID,
        sessionId: sessionID,
        sender: '',
        clientMessageId: '',
        language: '',
        storeId: '',
        conversationId: '',
      },
      pageContext: {
        pageType: pageType ?? '',
        referrerPage: '',
        timestamp: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
        url: ''
      },
    };
    let moderationResult;
    try {
      moderationResult = await getContentModeration(hosts?.apionline, imageData.file, uploadContext);
    } catch (err) {
      setUploadError(UPLOAD_ERROR_MESSAGES.GENERIC_ERROR);
      ctx.sendImageUploadAnalytics(sessionID, actionID, 'failure', userQuestion);
      setIsSubmitting(false);
      return false;
    }
    if (!moderationResult.moderationStatus) {
      setUploadError(UPLOAD_ERROR_MESSAGES.GENERIC_ERROR);
      ctx.sendImageUploadAnalytics(sessionID, actionID, 'failure', userQuestion);
      setIsSubmitting(false);
      return false;
    }
    if (moderationResult.moderationStatus !== 'VALID') {
      clearImage();
      setUploadError(UPLOAD_ERROR_MESSAGES.UNSUPPORTED_CONTENT_PROPRIETY);
      ctx.sendImageUploadAnalytics(sessionID, actionID, 'failure', userQuestion);
      setIsSubmitting(false);
      return false;
    }
    imageId = moderationResult.imageId;
    storageLink = moderationResult.storageLink;
  } else if (imageData && getIsEscalated()) {
    const userMessagePayload = createMessage(MessageType.USER, userQuestion, {
      messageOrigin,
      isImageAttached: true,
      ...(imageData?.imageOrigin && { imageOrigin: imageData.imageOrigin }),
      ...(imageData?.file?.name && { imageFileName: imageData.file.name }),
      ...(imageData?.aspectRatio && (() => {
        const { width, height } = getImageDimensions(imageData.aspectRatio, messageHistoryRef.current?.clientWidth);
        return { imageWidth: width, imageHeight: height };
      })()),
    });
    try {
      await saveMessage(userMessagePayload);
      await sendAgentImage(userQuestion, imageData.file, actionID);
      clearImage();
    } catch (err) {
      // The block reconciles and retries internally; a rejection here means it could not
      // reach a human at all, so say so rather than failing silently.
      logger.error(err);
      await saveMessage(LIVE_AGENT_ERROR_MESSAGE).catch((err1) => { logger.error(err1); });
    } finally {
      updateLastActivity();
      setIsSubmitting(false);
      broadCastMessageUpdate();
    }
    return Promise.resolve();
  }

  // Asking a question opens the drawer. Direct downward call (Seq D.3) — this used to post
  // MAA_OPEN_DRAWER onto the cross-tab BroadcastChannel for App's controller to hear.
  // `openDrawer` still notifies peer tabs; that hop is now owned by the drawer controller.
  openDrawer();

  setHistoryCleared(false);

  const userMessagePayload = createMessage(MessageType.USER, userQuestion, {
    messageOrigin,
    isImageAttached: !!imageData || !!retryStorageLink,
    ...(imageData?.imageOrigin && { imageOrigin: imageData.imageOrigin }),
    ...(imageData && { imageFileName: imageData.file?.name || 'user-uploaded image' }),
    ...(storageLink && { storageLink }),
    ...(imageId && { imageId }),
    ...(imageData?.aspectRatio && (() => {
      const { width, height } = getImageDimensions(imageData.aspectRatio, messageHistoryRef.current?.clientWidth);
      return { imageWidth: width, imageHeight: height };
    })()),
  });

  // ---- Idle TTL check — if escalation expired, disconnect and route to bot ----
  const isIdleExpired = checkIdleExpired();
  if (getIsEscalated() && isIdleExpired) {
    await handleIdleDisconnect();
    await saveMessage(LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE).catch((err) => { logger.error(err); });
  }

  // ---- Live-agent mode: route through Sprinklr instead of the bot API ----
  // Read INTENT from the store (single source of truth) so a stale closure cannot miss it.
  // Whether a connection is actually live is the block's business: `sendAgentMessage`
  // reconciles first (page reload, idle teardown, a peer tab's conversation). This flow used
  // to carry a second branch asking that question and driving the reconnect itself — that
  // was live-agent logic living in a flow, and it is gone.
  if (getIsEscalated()) {
    logger.log('[MAA] liveAgentHandoff detected — routing to the live-agent block');
    return saveMessage(userMessagePayload)
      .then(() => sendAgentMessage(userQuestion, actionID))
      .catch((err) => {
        logger.error(err);
        saveMessage(LIVE_AGENT_ERROR_MESSAGE).catch((err1) => { logger.error(err1); });
      })
      .finally(() => {
        updateLastActivity();
        broadCastMessageUpdate();
        setIsSubmitting(false);
      });
  }

  logger.log('[MAA] askUserQuestion — routing to BOT (isEscalated=false)');

  // ---- Normal bot flow ----
  return saveRetryMessage(isIdleExpired)
    .then(async () => {
      if (storageLink) {
        const imageLoaded = await isImageUrlValid(storageLink);
        if (!imageLoaded) {
          userMessagePayload.imageLoadFailed = true;
        }
      } else if (imageData) {
        userMessagePayload.imageLoadFailed = true;
      }
    })
    .then(() => saveMessage(userMessagePayload))
    .then((savedUserMessage) => {
      if (imageData) {
        clearImage();
      }
      return submitUserQuestionToApi({
        userQuestion: savedUserMessage.content,
        timestamp: savedUserMessage.timestamp,
        imageId,
        storageLink,
        actionID,
      });
    })
    .finally(() => {
      emit('magic-apron-assistant.question-ask', {
        userQuestion,
        messageOrigin,
        entryLocationForAnalytics,
        pageType,
        sessionID,
        actionID
      }, { frame: 'parent' });
    })
    .catch((err) => { logger.error(err); });
};
